import { motion } from "framer-motion";

const ease = [0.16, 1, 0.3, 1];

const DRAPE_IMG =
  "https://customer-assets.emergentagent.com/job_capital-motion/artifacts/9yrpnki8_ChatGPT%20Image%20Apr%2028%2C%202026%2C%2012_03_48%20AM.png";

const values = [
  ["Clarity, always", "Every dashboard, statement and message is engineered for instant understanding."],
  ["Privacy by design", "We treat your data and identity with the discretion private clients expect."],
  ["Institutional craft", "If a pixel is wrong, the trust is wrong. We sweat every detail of the experience."],
  ["Global by default", "Borders, currencies and time zones are no longer obstacles to managing your wealth."],
  ["Clients first, always", "We measure ourselves on relationship depth, not acquisition velocity."],
  ["Organic excellence", "We attract, develop and retain exceptional people who care."],
];

export default function Manifesto() {
  return (
    <section
      id="manifesto"
      className="relative bg-[#0A0B0D] py-24 sm:py-32 border-t border-[#1F2126]"
      data-testid="manifesto-section"
    >
      <div className="max-w-[1700px] mx-auto px-6 sm:px-10 lg:px-12">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-10% 0px" }}
          transition={{ duration: 1.6, ease }}
          className="relative frame aspect-[16/10] sm:aspect-[16/9] w-full overflow-hidden"
        >
          <img
            src={DRAPE_IMG}
            alt="Credit Stone Digital — built with intention"
            className="w-full h-full object-cover object-center"
            draggable="false"
          />

          <div className="absolute top-6 left-6 sm:top-8 sm:left-10 t-mono text-[#F4F2EC]">
            CSD AW26
            <br />
            <span className="text-[#F4F2EC]/65">Built with intention</span>
          </div>

          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-between px-6 sm:px-10 lg:px-16 t-mono text-[#F4F2EC]">
            <span>Discreet</span>
            <span>120+</span>
          </div>

          <div className="absolute inset-x-0 bottom-6 sm:bottom-8 flex items-center justify-between px-6 sm:px-10 lg:px-16 t-mono text-[#F4F2EC]">
            <span>20+</span>
            <span className="hidden sm:inline">CLIENTS — GLOBAL</span>
            <span>$6B+</span>
          </div>
        </motion.div>

        <div className="mt-20 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-3 t-mono text-[#7A7A78]">
            — § 08 / About us
          </div>
          <div className="col-span-12 md:col-span-9 max-w-3xl">
            <motion.h3
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 1.2, ease }}
              className="t-display text-[#F4F2EC] text-3xl sm:text-4xl lg:text-5xl leading-[1] tracking-[-0.035em]"
            >
              A private financial ecosystem,{" "}
              <span className="text-[#BFBDB6]">built with intention.</span>
            </motion.h3>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 1.2, ease, delay: 0.1 }}
              className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-10 max-w-2xl"
            >
              <p className="t-body text-sm text-[#BFBDB6] leading-relaxed">
                Credit Stone Digital serves modern global investors who want
                the freedom to bank, invest, and manage their portfolio
                digitally — anytime, anywhere.
              </p>
              <p className="t-body text-sm text-[#F4F2EC]/85 leading-relaxed">
                We obsess over every dashboard, every report, every interaction
                — until the experience finally feels institutional. Behind the
                scenes, we operate an event-sourced ledger powering portfolio
                analytics, investment mandate access, and partner-bank
                connectivity in real time.
              </p>
            </motion.div>

            <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl pt-8 border-t border-[#1F2126]">
              {[
                ["20+", "Countries served"],
                ["$6B+", "Portfolios tracked"],
                ["120+", "Private clients"],
                ["24/7", "Concierge access"],
              ].map((s) => (
                <div key={s[1]}>
                  <div className="t-display text-[#F4F2EC] text-2xl sm:text-3xl tracking-[-0.03em] font-light">
                    {s[0]}
                  </div>
                  <div className="mt-2 t-mono text-[#7A7A78]">{s[1]}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Values grid */}
        <div className="mt-28 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-3 t-mono text-[#7A7A78]">
            — Our values
          </div>
          <div className="col-span-12 md:col-span-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {values.map((v, i) => (
              <motion.div
                key={v[0]}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10% 0px" }}
                transition={{ duration: 0.8, ease, delay: i * 0.05 }}
                className="border-t border-[#1F2126] pt-6"
                data-testid={`value-${i}`}
              >
                <div className="t-mono text-[#7A7A78] mb-3">0{i + 1} / 06</div>
                <h4 className="t-body text-[#F4F2EC] text-lg tracking-[-0.02em] font-light">
                  {v[0]}
                </h4>
                <p className="mt-3 t-body text-sm text-[#BFBDB6] leading-relaxed">
                  {v[1]}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
